// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactTypography from "@keller/App/Components/Common/ContactTypography";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { getIsNonPurchasable } from "@keller/App/Store/Selectors/GeneralSelectors";
import signOutIcon from "@keller/XDL/Icons/SignOut";
import { CustomerHomeBranchAddressInfo } from "@keller/XDL/types";
import { xMask } from "@keller/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect } from "react-redux";

interface MiniSignInProps {
    linkGroups?: {
        title: string;
        links: (
            | {
                  title: string;
                  url: string;
                  type?: string | undefined;
                  id: string;
                  parentId?: string | undefined;
                  excludeFromNavigation?: boolean | undefined;
                  children?: PageLinkModel[] | undefined;
              }
            | undefined
        )[];
    }[];
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);
    const homeBranchAddress = session.properties.customerHomeBranch
        ? (JSON.parse(session.properties.customerHomeBranch) as CustomerHomeBranchAddressInfo)
        : null;
    return {
        currentBillTo: getCurrentBillToState(state).value,
        shipToFullAddress: session.properties.shipToFullAddress,
        displayChangeCustomerLink: session.displayChangeCustomerLink,
        changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
        isPunchOutSession: getIsPunchOutSession(state),
        branchEmail: homeBranchAddress?.branchEmail,
        branchPhone: homeBranchAddress?.branchPhone,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        isNonPurchasable: getIsNonPurchasable(state),
        warehouseDescription: homeBranchAddress?.description,
    };
};

type Props = MiniSignInProps & ReturnType<typeof mapStateToProps>;

export interface MiniMyAccountStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
    linkListContainer?: InjectableCss;
    linkItemsSubContainer?: InjectableCss;
    linkItem?: LinkPresentationProps;
    profileContainer?: InjectableCss;
    profileSubContainer?: InjectableCss;
    profileTitle?: TypographyPresentationProps;
    profileSubTitle?: TypographyPresentationProps;
    profileItemContainer?: InjectableCss;
    profileContactTypography?: TypographyPresentationProps;
    actionContainer?: InjectableCss;
    changeCustomerButton?: ButtonPresentationProps;
    signOutButton?: ButtonPresentationProps;
}

export const miniMyAccountStyles: MiniMyAccountStyles = {};

const MiniMyAccount: FC<Props> = ({
    branchPhone,
    displayChangeCustomerLink,
    changeCustomerUrl,
    branchEmail,
    myAccountPageLink,
    isNonPurchasable,
    warehouseDescription,
    isPunchOutSession,
    shipToFullAddress,
    currentBillTo,
    linkGroups,
    onSignOutHandler,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(miniMyAccountStyles, extendedStyles));
    const history = useHistory();

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                <StyledWrapper {...styles.linkListContainer} key={linkGroup.title}>
                    {linkGroup.title && <Typography {...styles.subtitle}>{linkGroup.title}</Typography>}
                    <StyledWrapper {...styles.linkItemsSubContainer}>
                        {linkGroup.links.map(
                            link =>
                                link?.url && (
                                    <StyledWrapper key={`${link?.title}_${link?.url}`}>
                                        <Link href={link?.url} {...styles.linkItem} key={`${link?.title}_${link?.url}`}>
                                            {link?.title}
                                        </Link>
                                    </StyledWrapper>
                                ),
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            );
        }
        return null;
    });

    return (
        <StyledWrapper {...styles.container}>
            <Clickable href={myAccountPageLink?.url || "/MyAccount"}>
                <Typography {...styles.title}>{translate("My Account")}</Typography>
            </Clickable>
            {linksSection}
            {/* Xng-changes : Hide Customer Information section on web-only */}
            {!isNonPurchasable && (
                <StyledWrapper {...styles.profileContainer}>
                    <Typography {...styles.subtitle}>{translate("Profile Information")}</Typography>
                    <StyledWrapper {...styles.profileSubContainer}>
                        <StyledWrapper {...styles.profileItemContainer}>
                            <Typography {...styles.profileTitle}>{translate("Customer")}</Typography>
                            {currentBillTo && (
                                <>
                                    <Typography {...styles.profileSubTitle}>{currentBillTo?.companyName}</Typography>
                                    {!isNonPurchasable && (
                                        <Typography {...styles.profileSubTitle}>
                                            {translate("Customer # {0}", currentBillTo?.customerNumber)}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.profileItemContainer}>
                            {!isNonPurchasable && (
                                <Typography {...styles.profileTitle}>{translate("Shipping Address")}</Typography>
                            )}
                            {shipToFullAddress && !isNonPurchasable && (
                                <>
                                    <Typography {...styles.profileSubTitle}>
                                        {parse(shipToFullAddress, parserOptions)}
                                    </Typography>
                                </>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.profileItemContainer}>
                            {(warehouseDescription || branchPhone) && (
                                <>
                                    <Typography {...styles.profileTitle}>{translate("Your Branch Info")}</Typography>
                                    {branchPhone && (
                                        <>
                                            <ContactTypography
                                                type="phone"
                                                targetValue={branchPhone}
                                                icon={true}
                                                {...styles.profileContactTypography}
                                            >
                                                {xMask.phoneNumber(branchPhone)}
                                            </ContactTypography>
                                        </>
                                    )}
                                    {warehouseDescription && (
                                        <>
                                            <ContactTypography
                                                type="email"
                                                targetValue={branchEmail}
                                                icon={true}
                                                {...styles.profileContactTypography}
                                            >
                                                {warehouseDescription}
                                            </ContactTypography>
                                        </>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {/* Xng-changes : Hide Change Ship-To on web-only */}
            <StyledWrapper {...styles.actionContainer}>
                {!isNonPurchasable && displayChangeCustomerLink && (
                    <Button
                        {...styles.changeCustomerButton}
                        aria-label="changeCustomer"
                        onClick={() => {
                            changeCustomerUrl && history.push(changeCustomerUrl);
                        }}
                    >
                        {translate("Change Ship-To")}
                    </Button>
                )}
                <Button
                    {...styles.signOutButton}
                    onClick={() => {
                        onSignOutHandler?.();
                    }}
                    aria-label="signOut"
                    data-test-selector="headerSignOutLink"
                >
                    <ButtonIcon src={signOutIcon} size={14} />
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(MiniMyAccount);
